import React, { useState, useEffect } from 'react';
import { getSettings, saveSettings, getVariableNames, getConversionTable, saveConversionTable, deleteConversionTable, getActiveThemes } from '../api';
import ConversionModal from '../components/ConversionModal';
import { useTheme } from '../context/ThemeContext';
import './Settings.css';

const TIMEZONES = [
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Moscow',
  'Europe/Kiev', 'Asia/Dubai', 'Asia/Tashkent', 'Asia/Yekaterinburg',
  'Asia/Novosibirsk', 'Asia/Krasnoyarsk', 'Asia/Irkutsk', 'Asia/Yakutsk',
  'Asia/Vladivostok', 'Asia/Kamchatka'
];

const createDefaultCamera = (index) => ({
  name: `Камера ${index}`,
  tempField: '',
  humidityField: '',
  lightField: '',
  tempRange: { min: -20, max: 10 },
  humidityRange: { min: 30, max: 70 },
  lightRange: { min: 0, max: 1000 },
  conversionTableId: null,
  tempDisplay: 'digits',
  humidityDisplay: 'digits',
  lightDisplay: 'digits',
  showTemperatureGraph: false
});

const createDefaultObject = (index) => ({
  name: index === undefined ? 'Мой склад' : `Новый объект ${index}`,
  imei: '',
  cameras: [createDefaultCamera(1)],
  gridColumns: 4,
  timezone: 'Europe/Moscow',
  isActive: true
});

const migrateCameraFields = (cam) => ({
  ...cam,
  tempRange: cam.tempRange || { min: -20, max: 10 },
  humidityRange: cam.humidityRange || { min: 30, max: 70 },
  lightRange: cam.lightRange || { min: 0, max: 1000 },
  tempDisplay: cam.tempDisplay || (cam.showTemperature === false ? 'hidden' : (cam.tempDisplayMode === 'progressbars' ? 'progressbars' : 'digits')),
  humidityDisplay: cam.humidityDisplay || (cam.showHumidity === false ? 'hidden' : (cam.humidityDisplayMode === 'progressbars' ? 'progressbars' : 'digits')),
  lightDisplay: cam.lightDisplay || (cam.showLight === false ? 'hidden' : (cam.lightDisplayMode === 'progressbars' ? 'progressbars' : 'digits')),
  showTemperatureGraph: cam.showTemperatureGraph === true
});

const Settings = () => {
  const [settings, setSettings] = useState({ objects: [], activeObjectId: null, theme: 'simple' });
  const [variableNames, setVariableNames] = useState([]);
  const [availableThemes, setAvailableThemes] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentCameraIndex, setCurrentCameraIndex] = useState(null);
  const [currentObjectIndex, setCurrentObjectIndex] = useState(null);
  const [conversionRows, setConversionRows] = useState([]);
  const [expandedObjects, setExpandedObjects] = useState({});
  const [expandedCameras, setExpandedCameras] = useState({});
  const { setTheme } = useTheme();

  useEffect(() => { loadSettings(); loadThemes(); }, []);

  const loadThemes = async () => {
    try {
      const themes = await getActiveThemes();
      setAvailableThemes(themes);
    } catch (err) {
      setAvailableThemes([{ name: 'simple', displayName: 'Простая' }, { name: 'dark', displayName: 'Тёмная' }, { name: 'light', displayName: 'Светлая' }]);
    }
  };

  const loadVariableNames = async (imei) => {
    if (imei) {
      try {
        const vars = await getVariableNames(imei);
        setVariableNames(Array.isArray(vars) ? vars : []);
      } catch (error) { setVariableNames([]); }
    } else setVariableNames([]);
  };

  const loadSettings = async () => {
    try {
      const data = await getSettings();
      if (!data.objects || !Array.isArray(data.objects)) data.objects = [];
      if (data.objects.length === 0) data.objects = [createDefaultObject()];
      else {
        data.objects = data.objects.map(obj => ({
          ...obj,
          cameras: (obj.cameras || []).map(cam => migrateCameraFields(cam))
        }));
      }
      if (!data.theme) data.theme = 'simple';
      setSettings(data);
      setTheme(data.theme);
      if (data.objects[0]?.imei) loadVariableNames(data.objects[0].imei);
    } catch (error) {
      setSettings({ objects: [createDefaultObject()], activeObjectId: null, theme: 'simple' });
      setTheme('simple');
    }
  };

  const toggleObject = (index) => setExpandedObjects(prev => ({ ...prev, [index]: !prev[index] }));
  const toggleCamera = (objIndex, camIndex) => setExpandedCameras(prev => ({ ...prev, [`${objIndex}-${camIndex}`]: !prev[`${objIndex}-${camIndex}`] }));

  const addObject = () => setSettings({ ...settings, objects: [...settings.objects, createDefaultObject(settings.objects.length + 1)] });
  const removeObject = (index) => {
    let updated = settings.objects.filter((_, i) => i !== index);
    if (updated.length === 0) updated = [createDefaultObject()];
    setSettings({ ...settings, objects: updated });
  };
  const handleObjectChange = (index, field, value) => {
    const updated = [...settings.objects];
    updated[index][field] = value;
    setSettings({ ...settings, objects: updated });
    if (field === 'imei') loadVariableNames(value);
  };
  const addCamera = (objIndex) => {
    const updated = [...settings.objects];
    updated[objIndex].cameras.push(createDefaultCamera(updated[objIndex].cameras.length + 1));
    setSettings({ ...settings, objects: updated });
  };
  const removeCamera = (objIndex, camIndex) => {
    const updated = [...settings.objects];
    updated[objIndex].cameras = updated[objIndex].cameras.filter((_, i) => i !== camIndex);
    setSettings({ ...settings, objects: updated });
  };
  const handleCameraChange = (objIndex, camIndex, field, value) => {
    const updated = [...settings.objects];
    updated[objIndex].cameras[camIndex][field] = value;
    setSettings({ ...settings, objects: updated });
  };
  const handleRangeChange = (objIndex, camIndex, param, bound, value) => {
    const updated = [...settings.objects];
    updated[objIndex].cameras[camIndex][`${param}Range`][bound] = Number(value);
    setSettings({ ...settings, objects: updated });
  };
  const handleThemeChange = (newTheme) => {
    setSettings(prev => ({ ...prev, theme: newTheme }));
    setTheme(newTheme);
  };

  const openConversionModal = async (objIndex, camIndex) => {
    setCurrentObjectIndex(objIndex);
    setCurrentCameraIndex(camIndex);
    const cameraId = settings.objects[objIndex].cameras[camIndex]._id;
    if (cameraId) {
      const table = await getConversionTable(cameraId);
      setConversionRows(table.rows || []);
    } else setConversionRows([]);
    setModalOpen(true);
  };
  const closeConversionModal = () => { setModalOpen(false); setCurrentObjectIndex(null); setCurrentCameraIndex(null); setConversionRows([]); };
  const saveConversionTableHandler = async (rows) => {
    if (currentObjectIndex === null || currentCameraIndex === null) return;
    const camera = settings.objects[currentObjectIndex].cameras[currentCameraIndex];
    if (!camera._id) { alert('Сначала сохраните настройки камеры'); return; }
    const result = await saveConversionTable(camera._id, rows);
    if (result.error) alert('Ошибка: ' + result.error);
    else {
      const updated = [...settings.objects];
      updated[currentObjectIndex].cameras[currentCameraIndex].conversionTableId = result._id;
      setSettings({ ...settings, objects: updated });
      closeConversionModal();
    }
  };
  const deleteConversionTableHandler = async () => {
    if (currentObjectIndex === null || currentCameraIndex === null) return;
    const camera = settings.objects[currentObjectIndex].cameras[currentCameraIndex];
    if (camera._id) {
      await deleteConversionTable(camera._id);
      const updated = [...settings.objects];
      updated[currentObjectIndex].cameras[currentCameraIndex].conversionTableId = null;
      setSettings({ ...settings, objects: updated });
    }
    closeConversionModal();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const preparedSettings = {
      ...settings,
      objects: settings.objects.map(obj => ({
        ...obj,
        cameras: obj.cameras.map(cam => ({
          ...cam,
          tempRange: cam.tempRange || { min: -20, max: 10 },
          humidityRange: cam.humidityRange || { min: 30, max: 70 },
          lightRange: cam.lightRange || { min: 0, max: 1000 },
          tempDisplay: cam.tempDisplay || 'digits',
          humidityDisplay: cam.humidityDisplay || 'digits',
          lightDisplay: cam.lightDisplay || 'digits'
        }))
      })),
      theme: settings.theme || 'simple'
    };
    try {
      const result = await saveSettings(preparedSettings);
      alert('Настройки сохранены');
      if (result.objects) setSettings(result);
      else loadSettings();
    } catch (error) { alert('Ошибка: ' + error.message); }
  };

  return (
    <div className="settings">
      <h2>Настройки системы</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-row theme-selector">
          <label>Оформление:</label>
          <select value={settings.theme} onChange={e => handleThemeChange(e.target.value)}>
            {availableThemes.map(t => <option key={t.name} value={t.name}>{t.displayName}</option>)}
          </select>
        </div>
        <div className="objects-container">
          {settings.objects.map((obj, objIdx) => (
            <div key={objIdx} className="object-card">
              <div className="object-header" onClick={() => toggleObject(objIdx)}>
                <div className="object-header-left">
                  <span className="expand-icon">{expandedObjects[objIdx] ? '▼' : '▶'}</span>
                  <h3>{obj.name || 'Без названия'}</h3>
                  <span className="object-imei">({obj.imei || 'IMEI не задан'})</span>
                </div>
                <button type="button" onClick={(e) => { e.stopPropagation(); removeObject(objIdx); }} className="remove-object-btn">Удалить объект</button>
              </div>
              {expandedObjects[objIdx] && (
                <div className="object-content">
                  <div className="form-row"><label>Название объекта:</label><input type="text" value={obj.name || ''} onChange={e => handleObjectChange(objIdx, 'name', e.target.value)} /></div>
                  <div className="form-row"><label>IMEI контроллера:</label><input type="text" value={obj.imei || ''} onChange={e => handleObjectChange(objIdx, 'imei', e.target.value)} /></div>
                  <div className="form-row"><label>Временная зона:</label><select value={obj.timezone} onChange={e => handleObjectChange(objIdx, 'timezone', e.target.value)}>{TIMEZONES.map(tz => <option key={tz}>{tz}</option>)}</select></div>
                  <div className="form-row"><label>Кол-во колонок:</label><input type="number" min="1" max="12" value={obj.gridColumns} onChange={e => handleObjectChange(objIdx, 'gridColumns', Number(e.target.value))} /></div>
                  <div className="form-row">
                    <label>Telegram токен:</label>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <code>{obj.telegramToken || '—'}</code>
                      <button type="button" onClick={() => navigator.clipboard.writeText(obj.telegramToken)} className="copy-btn">
                        📋 Копировать
                      </button>
                    </div>
                  </div>
                  <h4>Камеры</h4>
                  <div className="cameras-container">
                    {obj.cameras.map((cam, camIdx) => (
                      <div key={camIdx} className="camera-card-settings">
                        <div className="camera-header" onClick={() => toggleCamera(objIdx, camIdx)}>
                          <span className="expand-icon">{expandedCameras[`${objIdx}-${camIdx}`] ? '▼' : '▶'}</span>
                          <h5>{cam.name}</h5>
                          <button type="button" onClick={(e) => { e.stopPropagation(); removeCamera(objIdx, camIdx); }} className="remove-camera-btn">×</button>
                        </div>
                        {expandedCameras[`${objIdx}-${camIdx}`] && (
                          <div className="camera-content">
                            <div className="form-row"><label>Название камеры:</label><input type="text" value={cam.name} onChange={e => handleCameraChange(objIdx, camIdx, 'name', e.target.value)} /></div>
                            <div className="form-row"><label>Поле температуры:</label><select value={cam.tempField} onChange={e => handleCameraChange(objIdx, camIdx, 'tempField', e.target.value)}><option value="">— выберите —</option>{variableNames.map(n => <option key={n}>{n}</option>)}</select></div>
                            <div className="form-row range"><label>Диапазон температуры:</label><input type="number" step="any" value={cam.tempRange.min} onChange={e => handleRangeChange(objIdx, camIdx, 'temp', 'min', e.target.value)} /> – <input type="number" step="any" value={cam.tempRange.max} onChange={e => handleRangeChange(objIdx, camIdx, 'temp', 'max', e.target.value)} /> °C</div>
                            <div className="form-row"><label>Поле влажности:</label><select value={cam.humidityField} onChange={e => handleCameraChange(objIdx, camIdx, 'humidityField', e.target.value)}><option value="">— выберите —</option>{variableNames.map(n => <option key={n}>{n}</option>)}</select></div>
                            <div className="form-row range"><label>Диапазон влажности:</label><input type="number" step="any" value={cam.humidityRange.min} onChange={e => handleRangeChange(objIdx, camIdx, 'humidity', 'min', e.target.value)} /> – <input type="number" step="any" value={cam.humidityRange.max} onChange={e => handleRangeChange(objIdx, camIdx, 'humidity', 'max', e.target.value)} /> %</div>
                            <div className="form-row"><label>Поле освещённости:</label><select value={cam.lightField} onChange={e => handleCameraChange(objIdx, camIdx, 'lightField', e.target.value)}><option value="">— выберите —</option>{variableNames.map(n => <option key={n}>{n}</option>)}</select></div>
                            <div className="form-row range"><label>Диапазон освещённости:</label><input type="number" step="any" value={cam.lightRange.min} onChange={e => handleRangeChange(objIdx, camIdx, 'light', 'min', e.target.value)} /> – <input type="number" step="any" value={cam.lightRange.max} onChange={e => handleRangeChange(objIdx, camIdx, 'light', 'max', e.target.value)} /> лк</div>

                            {/* === Новые контролы отображения === */}
                            <div className="form-row display-row">
                              <label>🌡️ Температура:</label>
                              <div className="display-options">
                                <label><input type="radio" name={`temp-${objIdx}-${camIdx}`} value="hidden" checked={cam.tempDisplay === 'hidden'} onChange={() => handleCameraChange(objIdx, camIdx, 'tempDisplay', 'hidden')} /> Скрыть</label>
                                <label><input type="radio" name={`temp-${objIdx}-${camIdx}`} value="digits" checked={cam.tempDisplay === 'digits'} onChange={() => handleCameraChange(objIdx, camIdx, 'tempDisplay', 'digits')} /> Цифры</label>
                                <label><input type="radio" name={`temp-${objIdx}-${camIdx}`} value="progressbars" checked={cam.tempDisplay === 'progressbars'} onChange={() => handleCameraChange(objIdx, camIdx, 'tempDisplay', 'progressbars')} /> Прогресс-бар</label>
                              </div>
                            </div>
                            <div className="form-row display-row">
                              <label>💧 Влажность:</label>
                              <div className="display-options">
                                <label><input type="radio" name={`hum-${objIdx}-${camIdx}`} value="hidden" checked={cam.humidityDisplay === 'hidden'} onChange={() => handleCameraChange(objIdx, camIdx, 'humidityDisplay', 'hidden')} /> Скрыть</label>
                                <label><input type="radio" name={`hum-${objIdx}-${camIdx}`} value="digits" checked={cam.humidityDisplay === 'digits'} onChange={() => handleCameraChange(objIdx, camIdx, 'humidityDisplay', 'digits')} /> Цифры</label>
                                <label><input type="radio" name={`hum-${objIdx}-${camIdx}`} value="progressbars" checked={cam.humidityDisplay === 'progressbars'} onChange={() => handleCameraChange(objIdx, camIdx, 'humidityDisplay', 'progressbars')} /> Прогресс-бар</label>
                              </div>
                            </div>
                            <div className="form-row display-row">
                              <label>💡 Освещённость:</label>
                              <div className="display-options">
                                <label><input type="radio" name={`light-${objIdx}-${camIdx}`} value="hidden" checked={cam.lightDisplay === 'hidden'} onChange={() => handleCameraChange(objIdx, camIdx, 'lightDisplay', 'hidden')} /> Скрыть</label>
                                <label><input type="radio" name={`light-${objIdx}-${camIdx}`} value="digits" checked={cam.lightDisplay === 'digits'} onChange={() => handleCameraChange(objIdx, camIdx, 'lightDisplay', 'digits')} /> Цифры</label>
                                <label><input type="radio" name={`light-${objIdx}-${camIdx}`} value="progressbars" checked={cam.lightDisplay === 'progressbars'} onChange={() => handleCameraChange(objIdx, camIdx, 'lightDisplay', 'progressbars')} /> Прогресс-бар</label>
                              </div>
                            </div>
                            <div className="form-row graph-checkbox">
                              <label>
                                <input
                                  type="checkbox"
                                  checked={cam.showTemperatureGraph === true}
                                  onChange={(e) => handleCameraChange(objIdx, camIdx, 'showTemperatureGraph', e.target.checked)}
                                />
                                📈 Показывать график температуры за 24 часа
                              </label>
                            </div>
                            <div className="form-row"><button type="button" onClick={() => openConversionModal(objIdx, camIdx)} className="conversion-btn">{cam.conversionTableId ? 'Изменить таблицу конверсии' : 'Добавить таблицу конверсии'}</button></div>
                          </div>
                        )}
                      </div>
                    ))}
                    <button type="button" onClick={() => addCamera(objIdx)} className="add-camera-btn">+ Добавить камеру</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        <button type="button" onClick={addObject} className="add-object-btn">+ Добавить объект</button>
        <button type="submit" className="save-btn">Сохранить все настройки</button>
      </form>
      {modalOpen && <ConversionModal rows={conversionRows} onSave={saveConversionTableHandler} onDelete={deleteConversionTableHandler} onClose={closeConversionModal} />}
    </div>
  );
};

export default Settings;